import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, ScrollText, FileText, Check, X } from "lucide-react";
import { formatMoney, formatDate } from "@/lib/utils";
import { hasAnyRole, useAuth } from "@/lib/auth";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function ERP() {
  const { user } = useAuth();
  const summaryOnlyPayroll = user?.roles?.includes("principal") && !hasAnyRole(user.roles, ["platform_admin", "tenant_owner", "accountant"]);

  return (
    <div className="p-6 lg:p-8" data-testid="erp-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Enterprise operations</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">HR, payroll, leave, inventory</p>
      </div>
      <Tabs defaultValue="hr">
        <TabsList>
          <TabsTrigger value="hr" data-testid="tab-hr"><Users className="h-3.5 w-3.5 mr-1.5" /> HR</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><ScrollText className="h-3.5 w-3.5 mr-1.5" /> Payroll</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave"><FileText className="h-3.5 w-3.5 mr-1.5" /> Leave</TabsTrigger>
        </TabsList>
        <TabsContent value="hr"><HRTab /></TabsContent>
        <TabsContent value="payroll"><PayrollTab summaryOnly={summaryOnlyPayroll} /></TabsContent>
        <TabsContent value="leave"><LeaveTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function HRTab() {
  const { data, isLoading } = useQuery({ queryKey: ["staff-profiles"], queryFn: async () => unwrap(await api.get("/hr/staff-profiles")) });
  if (isLoading) return <Skeleton className="h-64 mt-6" />;
  return (
    <div className="mt-6">
      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">Staff directory</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead><tr>
              <th className="text-left px-3 py-3">Emp No.</th>
              <th className="text-left px-3 py-3">Name</th>
              <th className="text-left px-3 py-3">Designation</th>
              <th className="text-left px-3 py-3">Department</th>
              <th className="text-left px-3 py-3">Joined</th>
              <th className="text-right px-3 py-3">Annual CTC</th>
            </tr></thead>
            <tbody>
              {(data || []).map((p) => (
                <tr key={p.id} data-testid={`staff-${p.employee_no}`}>
                  <td className="px-3 py-3 font-mono-num text-xs">{p.employee_no}</td>
                  <td className="px-3 py-3 font-medium">{p.user?.full_name}</td>
                  <td className="px-3 py-3">{p.designation?.title || "—"}</td>
                  <td className="px-3 py-3 text-[var(--text-secondary)]">{p.department?.name || "—"}</td>
                  <td className="px-3 py-3 font-mono-num text-xs">{formatDate(p.date_of_joining)}</td>
                  <td className="px-3 py-3 text-right font-mono-num font-semibold">{formatMoney(p.ctc_paise_annual)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function PayrollTab({ summaryOnly = false }) {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["payroll-runs"], queryFn: async () => unwrap(await api.get("/payroll/runs")) });
  const [openRun, setOpenRun] = useState(null);
  const now = new Date();
  const nextMonth = { m: now.getMonth() + 1, y: now.getFullYear() };
  const runMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/payroll/runs", { period_month: nextMonth.m, period_year: nextMonth.y })),
    onSuccess: () => { toast.success("Payroll generated"); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const finalizeMut = useMutation({
    mutationFn: async (rid) => unwrap(await api.post(`/payroll/runs/${rid}/finalize`)),
    onSuccess: () => { toast.success("Finalized"); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading) return <Skeleton className="h-64 mt-6" />;

  return (
    <div className="mt-6 space-y-4">
      {!summaryOnly && <div className="flex justify-end">
        <Button onClick={() => runMut.mutate()} disabled={runMut.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="new-payroll-btn">
          <ScrollText className="h-4 w-4 mr-1.5" /> Run payroll · {MONTHS[nextMonth.m - 1]} {nextMonth.y}
        </Button>
      </div>}
      <Card className="border-[var(--subtle)]">
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead><tr>
              <th className="text-left px-3 py-3">Period</th>
              <th className="text-right px-3 py-3">Payslips</th>
              <th className="text-right px-3 py-3">Gross</th>
              <th className="text-right px-3 py-3">Net</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-left px-3 py-3">Action</th>
            </tr></thead>
            <tbody>
              {(data || []).map((r) => (
                <tr key={r.id} data-testid={`run-${r.id}`}>
                  <td className="px-3 py-3 font-medium">{MONTHS[r.period_month - 1]} {r.period_year}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{r.payslip_count}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{formatMoney(r.total_gross_paise)}</td>
                  <td className="px-3 py-3 text-right font-mono-num font-semibold text-[var(--brand)]">{formatMoney(r.total_net_paise)}</td>
                  <td className="px-3 py-3">
                    <Badge className={r.status === "finalized" ? "bg-[var(--success-bg)] text-[var(--success)] border-0" : "bg-[var(--warning-bg)] text-[var(--warning)] border-0"}>{r.status}</Badge>
                  </td>
                  <td className="px-3 py-3 space-x-2">
                    {!summaryOnly && <Button size="sm" variant="outline" className="h-8" onClick={() => setOpenRun(r.id)} data-testid={`view-${r.id}`}>View</Button>}
                    {!summaryOnly && r.status !== "finalized" && (
                      <Button size="sm" className="h-8 bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white" onClick={() => finalizeMut.mutate(r.id)} data-testid={`finalize-${r.id}`}>Finalize</Button>
                    )}
                  </td>
                </tr>
              ))}
              {(data || []).length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[var(--text-muted)]">No payroll runs yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {openRun && <PayslipsPanel rid={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
}

function PayslipsPanel({ rid, onClose }) {
  const { data } = useQuery({ queryKey: ["payslips", rid], queryFn: async () => unwrap(await api.get(`/payroll/runs/${rid}/payslips`)) });
  if (!data) return null;
  return (
    <Card className="border-[var(--subtle)]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-display">Payslips</CardTitle>
          <Button size="sm" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm grid-table">
          <thead><tr>
            <th className="text-left px-3 py-2">Employee</th>
            <th className="text-right px-3 py-2">Basic</th>
            <th className="text-right px-3 py-2">HRA</th>
            <th className="text-right px-3 py-2">Special</th>
            <th className="text-right px-3 py-2">PF</th>
            <th className="text-right px-3 py-2">Gross</th>
            <th className="text-right px-3 py-2">Net</th>
          </tr></thead>
          <tbody>
            {(data.payslips || []).map((s) => (
              <tr key={s.id}>
                <td className="px-3 py-2 font-medium">{s.user?.full_name}<div className="text-[10px] text-[var(--text-muted)] font-mono-num">{s.employee_no}</div></td>
                <td className="px-3 py-2 text-right font-mono-num">{formatMoney(s.basic_paise)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{formatMoney(s.hra_paise)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{formatMoney(s.special_paise)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-[var(--error)]">{formatMoney(s.pf_paise)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{formatMoney(s.gross_paise)}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold text-[var(--brand)]">{formatMoney(s.net_paise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function LeaveTab() {
  const { data, refetch } = useQuery({ queryKey: ["leave-requests"], queryFn: async () => unwrap(await api.get("/leave-requests")) });
  const decideMut = useMutation({
    mutationFn: async ({ id, decision }) => unwrap(await api.post(`/leave-requests/${id}/decision`, { decision })),
    onSuccess: () => { toast.success("Decided"); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6">
      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">Leave requests</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead><tr>
              <th className="text-left px-3 py-3">Staff</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-left px-3 py-3">From → To</th>
              <th className="text-right px-3 py-3">Days</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-left px-3 py-3">Action</th>
            </tr></thead>
            <tbody>
              {(data || []).map((r) => (
                <tr key={r.id} data-testid={`leave-${r.id}`}>
                  <td className="px-3 py-3 font-medium">{r.user?.full_name}</td>
                  <td className="px-3 py-3"><Badge className="bg-[var(--brand)] text-white border-0">{r.leave_type_code}</Badge></td>
                  <td className="px-3 py-3 font-mono-num text-xs">{formatDate(r.from_date)} → {formatDate(r.to_date)}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{r.days}</td>
                  <td className="px-3 py-3">
                    <Badge className={r.status === "approved" ? "bg-[var(--success-bg)] text-[var(--success)] border-0" : r.status === "rejected" ? "bg-[var(--error-bg)] text-[var(--error)] border-0" : "bg-[var(--warning-bg)] text-[var(--warning)] border-0"}>{r.status}</Badge>
                  </td>
                  <td className="px-3 py-3 space-x-2">
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" className="h-8 bg-[var(--success)] hover:bg-emerald-700 text-white" onClick={() => decideMut.mutate({ id: r.id, decision: "approved" })} data-testid={`approve-${r.id}`}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => decideMut.mutate({ id: r.id, decision: "rejected" })} data-testid={`reject-${r.id}`}><X className="h-3 w-3 mr-1" /> Reject</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(data || []).length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[var(--text-muted)]">No leave requests.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
